import { type DefaultRootState } from 'react-redux';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { type Draft } from 'immer';
import { type RememberedContext } from '@bbkAdminRedux/app/utils';
import {
  appPersistSlice,
  selectLastActive,
  type LastActiveState,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';

/**
 * The active company/app of *this browser tab*.
 *
 * `appPersist` writes the same record to `localStorage`, which every tab
 * shares: working in app B in a second tab would otherwise move the first tab
 * to app B the next time it resolves its context from storage. This tier is
 * persisted to `sessionStorage` instead, so each tab keeps its own tenant.
 *
 * It carries the same `email` as the browser-wide record, because a tab
 * outlives a session: signing out leaves `sessionStorage` intact, so without it
 * the next person to sign in here would resume the previous one's tenant
 * instead of their own.
 */
type TabPersistState = LastActiveState;

const initialState: TabPersistState = {};

export const tabPersistSlice = createSlice({
  name: 'tabPersist-rtk',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // One action, two tiers: the tab record cannot drift from `lastActive`.
    builder.addCase(
      appPersistSlice.actions.saveLastActive,
      (draftState: Draft<TabPersistState>, action) => {
        draftState.email = action.payload.email;
        draftState.companyKey = action.payload.companyKey;
        draftState.appKey = action.payload.appKey;
      }
    );
  },
});

const selectTabActive = (state: DefaultRootState) =>
  state[tabPersistSlice.name];

/**
 * The remembered tenant to resume, read through both tiers: this tab first,
 * then the browser-wide record. A brand new tab has no record of its own and
 * legitimately falls back.
 *
 * Both tiers are claimed by the address that wrote them, so neither can hand a
 * tenant to a different signed-in user.
 */
export const selectRememberedActiveContext = createSelector(
  [
    selectTabActive,
    selectLastActive,
    (_state: DefaultRootState, email?: string) => email,
  ],
  (tabActive, lastActive, email): RememberedContext | undefined => {
    const owned = (record?: LastActiveState) =>
      record?.email && record.email === email && record.companyKey
        ? { companyKey: record.companyKey, appKey: record.appKey }
        : undefined;

    return owned(tabActive) ?? owned(lastActive);
  }
);
